import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useEffect, useState } from "react";
import { Row, Col, Button, useZone, ComponentModal, InlineEdit, BoardFilters } from "../..";
import ResponsiveWidget from "../component/ResponsiveWidget";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { getComponents, saveTitle } from "../utils";
import ConfirmationModal from "../ConfirmationModal";
function Zone(props) {
    const { id, objectType, filter, onZoneReorder, deleteZone, ds, dataUrl } = props;
    const [draggable, setDraggable] = useState(false);
    const [currentWidgets, setCurrentWidgets] = useState([]);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const [editBtnsVisible, setEditBtnsVisible] = useState(false);
    const [saveLayout, setSaveLayout] = useState(false);
    const [title, setTitle] = useState(props.title);
    const z = useZone({
        endpoint: objectType,
        baseUrl: dataUrl,
        filter: filter,
        components: [],
    });
    const handleOpenModal = () => {
        setConfirmationModalOpen(true);
    };
    const handleBtnsVisible = () => {
        setEditBtnsVisible(!editBtnsVisible);
    };
    const confirmationModal = (_jsx(ConfirmationModal, { setOpen: setConfirmationModalOpen, open: confirmationModalOpen, onConfirmClick: () => deleteZone(id), itemType: "zone" }));
    useEffect(() => {
        getComponents(id, ds).then((components) => {
            // sort the widgets based on the order value
            const sortedWidgets = components.sort((a, b) => a.order - b.order);
            sortedWidgets.forEach((widget) => {
                z.zone.components[widget.componentId] = {
                    data: {
                        defaultFilter: widget.filter,
                        filter: widget.filter,
                        id: widget.componentId,
                        order: widget.order,
                        filterPassThrough: widget.filterPassThrough
                    },
                };
                z.zone.order.push(widget.componentId);
            });
            z.setZone({ ...z.zone });
            setCurrentWidgets(components);
        });
    }, []);
    const onAddComponent = () => {
        setOpen(true);
    };
    const editButton = (_jsx(Button, { outline: true, onClick: () => {
            setDraggable(!draggable);
        }, disabled: currentWidgets.length < 1, type: "edit", icon: "up-down-left-right", position: "right", tooltip: "Edit Widgets" }));
    const addButton = (_jsx(Button, { outline: true, onClick: () => {
            onAddComponent();
        }, type: "success", icon: "plus", position: "right", tooltip: "Add Widget" }));
    const deleteButton = (_jsx(Button, { outline: true, onClick: () => {
            handleOpenModal();
        }, type: "error", icon: "trash", position: "right", tooltip: "Delete Zone" }));
    const upButton = (_jsx(Button, { outline: true, onClick: async () => {
            await onZoneReorder(id, "up");
        }, type: "primary", icon: "arrow-up", position: "right", tooltip: "Move Zone Up" }));
    const downButton = (_jsx(Button, { outline: true, onClick: async () => {
            await onZoneReorder(id, "down");
        }, type: "primary", icon: "arrow-down", position: "right", tooltip: "Move Zone Down" }));
    const saveButton = (_jsx(Button, { onClick: () => {
            setDraggable(!draggable);
            setSaveLayout(true);
            setDraggable(false);
        }, type: "success", icon: "floppy-disk", position: "right", tooltip: "Save Layout" }));
    const filtersButton = (_jsx(Button, { outline: true, onClick: () => setOpenFilters(true), type: "primary", icon: "filter", position: "right", tooltip: "Add filters to the Zone" }));
    const showEditButtons = (_jsx(Button, { onClick: () => {
            handleBtnsVisible();
        }, type: editBtnsVisible ? "success" : "warning", icon: editBtnsVisible ? "check" : "pen-to-square", position: "right", tooltip: editBtnsVisible ? "Save Changes" : "Edit Zone", outline: !editBtnsVisible }));
    const buttons = (_jsx("div", { className: "tol-zone-bar", children: _jsxs(Row, { children: [_jsx(Col, { children: _jsx(InlineEdit, { title: title, onSave: (newTitle) => {
                            if (newTitle !== title) {
                                saveTitle(newTitle, ds, id, "zone");
                                setTitle(newTitle);
                            }
                        } }) }), _jsxs(Col, { children: [_jsx("h6", { children: !draggable ? (_jsxs(_Fragment, { children: [addButton, showEditButtons, editBtnsVisible ? (_jsxs(_Fragment, { children: [deleteButton, editButton, downButton, upButton] })) : null, filtersButton] })) : (_jsx(_Fragment, { children: saveButton })) }), _jsx("div", { id: "component-modal", children: _jsx(ComponentModal, { open: open, setOpen: setOpen, zoneId: id, ds: ds, currentWidgets: currentWidgets, setCurrentWidgets: setCurrentWidgets, dataUrl: dataUrl, ...z }) })] })] }) }));
    return (_jsxs("div", { className: "tol-zone", children: [buttons, currentWidgets.length > 0 ? (_jsx(ResponsiveWidget, { id: id, widgets: currentWidgets, setWidgets: setCurrentWidgets, draggable: draggable, zone: z.zone, setZone: z.setZone, saveLayout: saveLayout, setSaveLayout: setSaveLayout, ds: ds })) : (_jsx("div", { className: "tol-zone-empty", children: editBtnsVisible ? (_jsxs("p", { children: ["Click the", _jsx(FontAwesomeIcon, { icon: faPlus, size: "lg", style: { padding: "0 8" } }), "to add a new Component to the Zone."] })) : (_jsxs("div", { children: [_jsxs("p", { style: { marginBottom: "0" }, children: ["Click the", _jsx(FontAwesomeIcon, { icon: faPlus, size: "lg", style: { padding: "0 8" } }), "to add a new Component to the Zone."] }), _jsxs("p", { children: ["Click the", _jsx(FontAwesomeIcon, { icon: faPenToSquare, size: "lg", style: { padding: "0 8" } }), "to edit the Zone."] })] })) })), confirmationModal, _jsx(BoardFilters, { id: id, entityType: "zone", open: openFilters, setOpen: setOpenFilters, ...z })] }));
}
export default Zone;
